import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Plus, Trash2, Pin, Edit2, X, Check } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const TYPES = [
  { value: 'info', label: 'ℹ️ Information', color: 'bg-navy-100 text-navy-700' },
  { value: 'success', label: '✅ Bonne nouvelle', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'warning', label: '⚠️ Attention', color: 'bg-amber-100 text-amber-700' },
  { value: 'danger', label: '🚨 Urgent', color: 'bg-red-100 text-red-700' },
];

const CATEGORIES = [
  { value: 'reglementaire', label: 'Réglementaire' },
  { value: 'financement', label: 'Financement' },
  { value: 'sante', label: 'Santé' },
  { value: 'rh', label: 'RH' },
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'autre', label: 'Autre' },
];

const TYPE_BADGE = {
  info: 'bg-navy-100 text-navy-700',
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
};

const EMPTY_FORM = {
  titre: '', contenu: '', type: 'info', categorie: 'reglementaire',
  source: '', source_url: '', date_effet: '', epingle: false
};

function AlerteForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputClass = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';
  const labelClass = 'block text-xs font-medium text-slate-600 mb-1';

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4">
      <h3 className="font-semibold text-slate-700">{initial?.id ? 'Modifier l\'alerte' : 'Nouvelle alerte'}</h3>

      <div>
        <label className={labelClass}>Titre *</label>
        <input value={form.titre} onChange={e => set('titre', e.target.value)}
          className={inputClass} placeholder="Ex: CMG revalorisé au 1er avril 2026" required />
      </div>

      <div>
        <label className={labelClass}>Message *</label>
        <textarea value={form.contenu} onChange={e => set('contenu', e.target.value)}
          rows={4} className={inputClass} placeholder="Expliquez le contexte, l'impact et les actions à prendre..." required />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Type</label>
          <select value={form.type} onChange={e => set('type', e.target.value)} className={inputClass}>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Catégorie</label>
          <select value={form.categorie} onChange={e => set('categorie', e.target.value)} className={inputClass}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Source</label>
          <input value={form.source} onChange={e => set('source', e.target.value)}
            className={inputClass} placeholder="Ex: CAF, Légifrance, ANSES" />
        </div>
        <div>
          <label className={labelClass}>Date d'effet</label>
          <input type="date" value={form.date_effet} onChange={e => set('date_effet', e.target.value)}
            className={inputClass} />
        </div>
      </div>

      <div>
        <label className={labelClass}>URL de la source</label>
        <input value={form.source_url} onChange={e => set('source_url', e.target.value)}
          className={inputClass} placeholder="https://www.legifrance.gouv.fr/..." />
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.epingle} onChange={e => set('epingle', e.target.checked)}
          className="w-4 h-4 rounded accent-primary-500" />
        <span className="text-sm text-slate-700">📌 Épingler cette alerte (s'affiche en priorité)</span>
      </label>

      <div className="flex gap-2 pt-1">
        <button onClick={() => onSave(form)}
          className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2">
          <Check size={15} /> {initial?.id ? 'Enregistrer' : 'Publier l\'alerte'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm">
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function AlertesPage() {
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/alertes');
      setAlertes(res.data || []);
    } catch { setAlertes([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await axios.put(`/api/alertes/${editing.id}`, { ...form, active: 1 });
      } else {
        await axios.post('/api/alertes', form);
      }
      setShowForm(false); setEditing(null);
      load();
    } catch { alert('Erreur lors de l\'enregistrement'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archiver cette alerte ?')) return;
    await axios.delete(`/api/alertes/${id}`);
    load();
  };

  const handleTogglePin = async (alerte) => {
    await axios.put(`/api/alertes/${alerte.id}`, { ...alerte, epingle: alerte.epingle ? 0 : 1, active: 1 });
    load();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
            <Bell size={18} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Alertes réglementaires</h1>
            <p className="text-slate-500 text-sm">Gérez les notifications affichées à votre équipe</p>
          </div>
        </div>
        {!showForm && (
          <button onClick={() => { setShowForm(true); setEditing(null); }}
            className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors">
            <Plus size={16} /> Nouvelle alerte
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-navy-50 border border-navy-200 rounded-xl px-4 py-3 mb-6 text-sm text-navy-700">
        💡 Les alertes épinglées s'ouvrent automatiquement quand un membre de l'équipe se connecte. Les autres sont accessibles via la cloche 🔔.
      </div>

      {/* Formulaire */}
      {(showForm || editing) && (
        <div className="mb-6">
          <AlerteForm
            initial={editing}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : alertes.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-200">
          <Bell size={36} className="mx-auto mb-3 text-slate-200" />
          <p className="text-slate-400 text-sm">Aucune alerte. Créez-en une pour notifier votre équipe.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alertes.map(alerte => (
            <div key={alerte.id} className="bg-white border border-slate-200 rounded-xl px-5 py-4 shadow-sm flex gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {alerte.epingle === 1 && <span className="text-xs bg-slate-800 text-white px-2 py-0.5 rounded-full">📌 Épinglé</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[alerte.type] || 'bg-slate-100 text-slate-600'}`}>
                    {TYPES.find(t => t.value === alerte.type)?.label || alerte.type}
                  </span>
                  <span className="text-xs text-slate-400">{CATEGORIES.find(c => c.value === alerte.categorie)?.label}</span>
                  {alerte.date_effet && (
                    <span className="text-xs text-slate-400">
                      · {new Date(alerte.date_effet).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
                <p className="font-semibold text-slate-800 text-sm">{alerte.titre}</p>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{alerte.contenu}</p>
                {alerte.source && <p className="text-xs text-slate-400 mt-1">Source : {alerte.source}</p>}
                <p className="text-xs text-slate-300 mt-1">
                  Créée le {format(new Date(alerte.created_at), 'd MMM yyyy', { locale: fr })}
                </p>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button onClick={() => handleTogglePin(alerte)} title={alerte.epingle ? 'Désépingler' : 'Épingler'}
                  className={`p-2 rounded-lg text-sm transition-colors ${alerte.epingle ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-100'}`}>
                  <Pin size={14} />
                </button>
                <button onClick={() => { setEditing(alerte); setShowForm(false); }}
                  className="p-2 text-slate-400 hover:text-navy-700 hover:bg-navy-50 rounded-lg transition-colors">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(alerte.id)}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
